import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router, Route } from '@angular/router';
import { Observable, from, of } from 'rxjs';
import { SuperstructureComponent } from 'src/app/gigmonster/components/superstructure/superstructure.component';
import { OrgInfo } from 'src/app/base/interfaces/org-info';
import { SuperstructureEasysuiteComponent } from 'src/app/easysuite/components/easysuite-superstructure/easysuite-superstructure.component';
import { environment } from '../../../../environments/environment';
import { ContentService } from '../content/content.service';
import { AppidService } from '../appid/appid.service';
import { AuthService } from '../auth/auth.service';
import { switchMap, map, take } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class NavService {
    superstructureType: any = null;
    private appID: number;

    constructor(
        private http: HttpClient,
        private router: Router,
        public content: ContentService,
        public route: ActivatedRoute,
        private appidService: AppidService,
        private auth: AuthService
    ) {
        // Since NavService is a service, it need to only takes the first value emitted by appID$ and then automatically unsubscribes.
        this.appidService.appID$.pipe(take(1)).subscribe((appID) => {
            this.appID = appID;
            this.setComponentTypes();
        });
    }

    /**
     * Sets the component types based on the appID.
     * This method determines which components should be used for the application's 
     * superstructure and login process depending on the current appID.
     * This function will go away when all apps are merged into one superstructure
     */
    private setComponentTypes() {
        // If the app ID matches 434693, set the components to GigMonster.
        if (this.appID === 434693) {
            this.superstructureType = SuperstructureComponent;
        }

        // For any other appID, use the Easysuite components.
        else {
            this.superstructureType = SuperstructureEasysuiteComponent;
        }
    }

    /**
     * Loads navigation routes from the local storage cache and sets up the router configuration.
     * @returns {boolean} Returns true if the routes were loaded successfully, false otherwise.
     */
    loadNavsByCache(): boolean {
        try {
            const navs = JSON.parse(localStorage.getItem("routes"));
            if (!navs) {
                return true;
            }

            // Iterate through the stored routes to update their components dynamically.
            navs.forEach(nav => {
                if (!nav.redirectTo) {
                    nav.component = this.superstructureType;
                }
            });

            // Apply the retrieved and modified routes to the Angular router configuration.
            this.router.resetConfig(navs);
            return true;
        } catch (error) {
            console.warn(error);
            return false;
        }
    }


    /**
     * Fetches navigation routes from the BE and updates the router configuration dynamically.
     * This function is called when a user logins in so that the router has the correct routes for that role.
     * @param {string | null} authToken Never used in this function, it needs to be clarified what it is used for
     * @param {number} roleID
     * @param {string} [SID]
     * @returns {Observable<any>} An observable that emits the updated navigation configuration.
     */
    updateNavs(authToken: string | null, roleID: number, SID?: string): Observable<any> {
        const url = `${environment.urls.core}/navs`;
        let httpParams = new HttpParams()
            .set('app_id', this.appID)
            .set('role_id', roleID);
        
        // SID will only exist if coming from V1, the following 2 variables are added to params because it is needed by the CheckAuth() method on the BE.
        if (SID) {
            httpParams = httpParams.set('sid', SID);
            httpParams = httpParams.set('V1UserID', localStorage.getItem('V1UserID'));
        }

        // The only time this org is set  is when we are already in an auth role and have been logged in through V2.
        // Org ID will not be set if we are in an unauth role.
        if (localStorage.getItem("org")) {

            httpParams = httpParams.set('org_id', localStorage.getItem("org"));
        }
        // Else if there is no V2 set org, and we have an SID,
        // then we came from V1 and should have a V1 org ID to use
        else if (SID) {
            httpParams = httpParams.set('org_id', localStorage.getItem("V1OrgID"));
        }
        
        return this.http.get(url, {
            params: httpParams,
            headers: {
                'authorization': authToken ? authToken : 'unknown person'
            }
        }).pipe(
            switchMap((response: any) => {

				// Clear the existing router configuration before adding new routes.
                this.router.resetConfig([]);

                // Adds the login route as the first route.
                this.router.config.unshift({ path: 'auth/login', component: this.superstructureType });

				// This wildcard route is added to catch any undefined routes and redirect them to the login page. This prevents a hack.
                this.router.config.push({ path: '**', redirectTo: 'auth/login' });

                // Add the navs and their children to the router.
                response["navs"].forEach(nav => {
                    this.router.config.unshift(...this.recurseNavs(nav));
                });

				// Set router and local storage with new routes. Need local storage bc simple browser refresh will wipe the router config clean since it's stored in memory.
                this.router.resetConfig(this.router.config);
                localStorage.setItem("routes", JSON.stringify(this.router.config));

                return of(response);
            })
        );
    }

    /**
     * Determines the navigation for an unauthenticated user based on query parameters.
     * If a session ID (`sid`) is found in the query params, it fetches the role and updates navigation.
     * Otherwise, it uses the stored user role.
     * @returns {Observable<any>} An observable that emits the updated navigation configuration.
     */
    getUnauthNavs(): Observable<any> {
        return this.route.queryParams.pipe(
            switchMap(params => {

                // sid comes in the params when the user is redirected from V1 to V2. 
                if (params['sid']) {
                    const SID = params['sid'];

                    // V1UserID is needed within the updateNavs function used.
                    // This location is only hit when user comes from V1 -> V2 for the first time ever and there is nothing stored for routes yet.
                    localStorage.setItem('V1UserID', params['V1UserID']);

                    // Convert the promise to an observable using `from()`
                    return this.http.get<any>(`${environment.urls.core}/auth/getrole?sid=${SID}`).pipe(
                        // Call `updateNavs` with the response data
                        switchMap(response => this.updateNavs(response.token, response.role_id, SID))
                    );
                }
                // Fallback in case no `sid` is provided and when the user access the v2 directly, this else block will be executed. 
                else {
                    let role = this.auth.getRole();

                    // The below if condition was coupled with '&& !isNaN(role)'. adding this comment just 
                    // to make sure this change was there incase anything breaks in the future.
                    if (isNaN(role)) {
                        role = 0;
                    }
                    return this.updateNavs(null, role);
                }
            })
        );
    }

    getUnauthFirstNav(appID) {
        let httpParams = new HttpParams()
            .set('app_id', this.appID)

        return this.http.get<OrgInfo>(`${environment.urls.core}/navs/getunauthnav`, {
            params: httpParams,
            
            // Unknown person has two purposes; here it works when unauth.
            headers: { 'authorization': 'unknown person' }
        });
    }

    /**
     * Clears and refreshes the navigation configuration.
     * This is used primarily on logout/ initial site visit, it removes all routes from the router and adds in the unauth routes
     * @returns {Observable<any>} An observable that emits the updated navigation configuration.
     */
    clearNavs(): Observable<any> {
        return new Observable(observer => {
            this.getUnauthNavs().subscribe(
                (response) => {
                    const currentUrl = this.router.url;

                    //This is a Temporary Solution. 
                    //the below URLs needs to be redirected without the help of cached NAVs during
                    // situations like "Forgot Password" or "Register via invite"
                    const allowedUnauthUrls = [
                        '/venues/register',
                        '/venues/forgotpassword',
                        '/performers/register',
                        '/performers/forgotpassword'
                    ];

                    // If the current URL is in the allowed list, reload it, if not,
                    // reset the router config and store the new navigation.
                    if (allowedUnauthUrls.some(url => currentUrl.includes(url))) {
                        this.router.navigateByUrl(currentUrl);
                    } else {
                        this.router.resetConfig(this.router.config);
                        localStorage.setItem("routes", JSON.stringify(this.router.config));
                        observer.next(response);
                    }
                },
                (error) => {
                    console.error("Failed to update the navs:", error);
                    observer.error(error);
                }
            );
        });
    }

    /**
     * Retrieves the organization name based on the organization ID.
     * @param {number} orgID
     * @param {string} authToken
     * @returns {Observable<OrgInfo>} An observable that emits the organization information.
     */
    getHeaderInfo(orgID: number, authToken: string, NavSourceV1?: string, V1SID?: string, V1OrgID?: string, V1UserID?: string): Observable<OrgInfo> {
        let httpParams = new HttpParams()
            .set('app_id', this.appID)
            .set('org_id', orgID);

        if (NavSourceV1) {
            httpParams = httpParams.set('NavSourceV1', NavSourceV1).set('sid', V1SID).set('org_id', V1OrgID).set('V1UserID', V1UserID);
        }

        return this.http.get<OrgInfo>(`${environment.urls.core}/navs/header`, {
            params: httpParams,
            
            // Unknown person has two purposes; here it works when the user comes from V1
            headers: { 'authorization': authToken ? authToken : 'unknown person' }
        });
    }

    // This function is used when user is unauth but exists a org_id in the URL
    // to display the Org logo in the header.
    getOrgLogo(orgID) {
        let httpParams = new HttpParams()
            .set('app_id', this.appID)
            .set('org_id', orgID);
            
        return this.http.get<OrgInfo>(`${environment.urls.core}/navs/getorglogo`, {
            params: httpParams,
            headers: { 'authorization': 'unknown person' }
        });
    }

    /**
     * Recursively processes navigation routes and generates an array of Angular route objects.
     * @param {any} parentNav
     * @param {string} [parentLabel=""]
     * @returns {Route[]} An array of route objects.
     */
    private recurseNavs(parentNav: any, parentLabel = ""): Route[] {
        let routes: Route[] = [];
        let label = parentLabel ? `${parentLabel}/${parentNav["label"]}` : parentNav["label"];
        label = label.replace(/\s/g, "").toLowerCase();

        routes.push({
            path: label,
            component: this.superstructureType,
            data: {
                nav: parentNav["id"],
                label: parentNav["label"],
                hasPage: parentNav["hasPage"],
                sortOrder: parentNav["sort_order"],
                navPage: parentNav["page"]
            }
        });

        parentNav["children"].forEach(childNav => {
            routes.push(...this.recurseNavs(childNav, label));
        });

        return routes;
    }
}
